/**
 * App Version Service
 * Checks if the current app version meets minimum requirements
 * Used to force updates for native iOS/Android apps
 */

import { App } from '@capacitor/app';
import { isNativeApp, isNativeIOS, isNativeAndroid } from '../utils/platform.utils';
import { API_CONFIG } from '../config/api.config';

export interface VersionInfo {
  ios: {
    min_version: string;
    min_build: number;
    current_version: string;
    current_build: number;
    store_url: string;
  };
  android: {
    min_version: string;
    min_build: number;
    current_version: string;
    current_build: number;
    store_url: string;
  };
  force_update: boolean;
  update_message: {
    en: string;
    es: string;
  };
  maintenance_mode: boolean;
  maintenance_message: {
    en: string;
    es: string;
  };
}

export interface UpdateCheckResult {
  updateRequired: boolean;
  maintenanceMode: boolean;
  storeUrl: string;
  message: string;
  currentBuild: number;
  minBuild: number;
}

class AppVersionService {
  private cachedVersionInfo: VersionInfo | null = null;
  private lastCheck: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Check if the current app version meets minimum requirements
   * Returns null for PWA (no version check needed)
   */
  async checkVersion(): Promise<UpdateCheckResult | null> {
    // Only check for native apps
    if (!isNativeApp()) {
      return null;
    }

    try {
      // Get server version requirements
      const versionInfo = await this.fetchVersionInfo();
      if (!versionInfo) {
        console.log('[AppVersion] Could not fetch version info');
        return null;
      }

      // Check maintenance mode first
      if (versionInfo.maintenance_mode) {
        const lang = localStorage.getItem('language') || 'en';
        return {
          updateRequired: false,
          maintenanceMode: true,
          storeUrl: '',
          message: versionInfo.maintenance_message[lang as 'en' | 'es'] || versionInfo.maintenance_message.en,
          currentBuild: 0,
          minBuild: 0
        };
      }

      // Get current app info
      const appInfo = await App.getInfo();
      const currentBuild = parseInt(appInfo.build, 10);

      // Get platform-specific requirements
      const platform = isNativeIOS() ? 'ios' : 'android';
      const platformInfo = versionInfo[platform];

      const lang = localStorage.getItem('language') || 'en';
      const message = versionInfo.update_message[lang as 'en' | 'es'] || versionInfo.update_message.en;

      // Check if force update is enabled globally
      if (versionInfo.force_update) {
        return {
          updateRequired: true,
          maintenanceMode: false,
          storeUrl: platformInfo.store_url,
          message,
          currentBuild,
          minBuild: platformInfo.min_build
        };
      }

      // Check if current build is below minimum
      if (currentBuild < platformInfo.min_build) {
        console.log(`[AppVersion] Update required: current build ${currentBuild} < min build ${platformInfo.min_build}`);
        return {
          updateRequired: true,
          maintenanceMode: false,
          storeUrl: platformInfo.store_url,
          message,
          currentBuild,
          minBuild: platformInfo.min_build
        };
      }

      console.log(`[AppVersion] App is up to date: build ${currentBuild} >= ${platformInfo.min_build}`);
      return {
        updateRequired: false,
        maintenanceMode: false,
        storeUrl: platformInfo.store_url,
        message: '',
        currentBuild,
        minBuild: platformInfo.min_build
      };

    } catch (error) {
      console.error('[AppVersion] Error checking version:', error);
      return null;
    }
  }

  /**
   * Fetch version info from server with caching
   */
  private async fetchVersionInfo(): Promise<VersionInfo | null> {
    // Return cached version if still valid
    const now = Date.now();
    if (this.cachedVersionInfo && (now - this.lastCheck) < this.CACHE_DURATION) {
      return this.cachedVersionInfo;
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/app/version`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        this.cachedVersionInfo = data.data;
        this.lastCheck = now;
        return data.data;
      }

      return null;
    } catch (error) {
      console.error('[AppVersion] Failed to fetch version info:', error);
      return null;
    }
  }

  /**
   * Open the appropriate app store
   */
  async openAppStore(): Promise<void> {
    const platform = isNativeIOS() ? 'ios' : 'android';
    const versionInfo = await this.fetchVersionInfo();

    if (versionInfo) {
      const storeUrl = versionInfo[platform].store_url;
      window.open(storeUrl, '_system');
    }
  }
}

export const appVersionService = new AppVersionService();
